import { supabase } from '../supabaseClient';
import { getRoutingConfig } from './routingEngineService';

const HOME_FUEL_TANKS = [
  {
    id: 'home-tank-1',
    name: 'HM Hauling Yard - Main Tank',
    address: '123 Industrial Way, Edmonton, AB',
    lat: 53.5461,
    lng: -113.4938,
    fuelTypes: ['diesel', 'dyed_diesel'],
    capacity: 10000,
    currentLevel: 7500,
    pricePerLiter: 1.25,
    isHomeTank: true
  },
  {
    id: 'home-tank-2',
    name: 'HM Hauling Yard - Secondary Tank',
    address: '456 Trucking Blvd, Calgary, AB',
    lat: 51.0447,
    lng: -114.0719,
    fuelTypes: ['diesel'],
    capacity: 5000,
    currentLevel: 3200,
    pricePerLiter: 1.28,
    isHomeTank: true
  }
];

export const getHomeFuelTanks = () => {
  return HOME_FUEL_TANKS;
};

export const findNearestFuelStop = async (currentLocation, fuelType = 'diesel', preferHomeTank = true) => {
  const results = [];
  
  if (preferHomeTank) {
    const homeTanks = HOME_FUEL_TANKS.filter(tank => 
      tank.fuelTypes.includes(fuelType) && tank.currentLevel > 100
    );
    
    homeTanks.forEach(tank => {
      const distance = calculateDistance(
        currentLocation.lat, 
        currentLocation.lng, 
        tank.lat, 
        tank.lng
      );
      results.push({
        ...tank,
        distance,
        estimatedTime: Math.round(distance / 80 * 60),
        source: 'home_tank'
      });
    });
  }
  
  const googlePlacesStops = await searchGooglePlacesFuelStations(currentLocation, fuelType);
  results.push(...googlePlacesStops);
  
  results.sort((a, b) => {
    if (a.isHomeTank && !b.isHomeTank) return -1;
    if (!a.isHomeTank && b.isHomeTank) return 1;
    return a.distance - b.distance;
  });
  
  return results;
};

const searchGooglePlacesFuelStations = async (location, fuelType) => {
  const config = getRoutingConfig();
  const googleMapsApiKey = config.googleMapsApiKey;
  
  if (!googleMapsApiKey) {
    return getMockFuelStations(location);
  }
  
  try {
    // __BOARD_MAPS_KEY__ the REST Places endpoint refuses a website-restricted
    // browser key (and is CORS-blocked in a browser anyway), so search through
    // the Maps JavaScript API's PlacesService, loaded with the shared key.
    const { ensureGoogleMaps } = await import('./mapsKeyService');
    const gmaps = await ensureGoogleMaps();
    if (!gmaps.places || !gmaps.places.PlacesService) throw new Error('Places library not loaded');
    const svc = new gmaps.places.PlacesService(document.createElement('div'));
    const places = await new Promise((resolve, reject) => {
      svc.nearbySearch({ location: { lat: location.lat, lng: location.lng }, radius: 50000, type: 'gas_station' },
        (res, status) => (status === 'OK' || status === 'ZERO_RESULTS') ? resolve(res || []) : reject(new Error('Places: ' + status)));
    });

    return places.slice(0, 10).map(place => {
      const plat = place.geometry.location.lat();
      const plng = place.geometry.location.lng();
      return {
        id: place.place_id,
        name: place.name,
        address: place.vicinity,
        lat: plat,
        lng: plng,
        fuelTypes: ['diesel', 'gasoline'],
        pricePerLiter: null,
        isHomeTank: false,
        distance: calculateDistance(location.lat, location.lng, plat, plng),
        rating: place.rating,
        source: 'google_places'
      };
    });
  } catch (error) {
    console.error('Error fetching fuel stations from Google Places:', error);
    return getMockFuelStations(location);
  }
};

const getMockFuelStations = (location) => {
  const mockStations = [
    { name: 'Petro-Canada', offset: { lat: 0.02, lng: 0.03 } },
    { name: 'Shell', offset: { lat: -0.015, lng: 0.025 } },
    { name: 'Esso', offset: { lat: 0.025, lng: -0.02 } },
    { name: 'Husky', offset: { lat: -0.03, lng: -0.015 } },
    { name: 'Co-op Gas Bar', offset: { lat: 0.01, lng: -0.035 } }
  ];
  
  return mockStations.map((station, index) => {
    const lat = location.lat + station.offset.lat;
    const lng = location.lng + station.offset.lng;
    const distance = calculateDistance(location.lat, location.lng, lat, lng);
    
    return {
      id: `mock-station-${index}`,
      name: station.name,
      address: `Near ${location.lat.toFixed(2)}, ${location.lng.toFixed(2)}`,
      lat,
      lng,
      fuelTypes: ['diesel', 'gasoline'],
      pricePerLiter: 1.35 + (Math.random() * 0.2),
      isHomeTank: false,
      distance,
      estimatedTime: Math.round(distance / 60 * 60),
      source: 'mock'
    };
  });
};

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);

export const logFuelStop = async (driverId, truckId, fuelStopData) => {
  try {
    const { data, error } = await supabase
      .from('fuel_stops')
      .insert([{
        driver_id: driverId,
        truck_id: truckId,
        fuel_station_id: fuelStopData.stationId,
        fuel_station_name: fuelStopData.stationName,
        is_home_tank: fuelStopData.isHomeTank,
        fuel_type: fuelStopData.fuelType,
        liters: fuelStopData.liters,
        price_per_liter: fuelStopData.pricePerLiter,
        total_cost: fuelStopData.liters * fuelStopData.pricePerLiter,
        odometer_km: fuelStopData.odometerKm,
        location_lat: fuelStopData.lat,
        location_lng: fuelStopData.lng,
        notes: fuelStopData.notes,
        created_at: new Date().toISOString()
      }]);
    
    if (error) throw error;
    
    if (fuelStopData.isHomeTank) {
      const tank = HOME_FUEL_TANKS.find(t => t.id === fuelStopData.stationId);
      if (tank) {
        tank.currentLevel -= fuelStopData.liters;
      }
    }
    
    return data;
  } catch (error) {
    console.error('Error logging fuel stop:', error);
    throw error;
  }
};

export const getFuelHistory = async (truckId, limit = 20) => {
  try {
    const { data, error } = await supabase
      .from('fuel_stops')
      .select('*')
      .eq('truck_id', truckId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching fuel history:', error);
    return [];
  }
};

export const calculateFuelEfficiency = async (truckId) => {
  try {
    const fuelStops = await getFuelHistory(truckId, 10);
    
    if (fuelStops.length < 2) {
      return null;
    }
    
    const sortedStops = fuelStops.sort((a, b) => a.odometer_km - b.odometer_km);
    
    let totalKm = 0;
    let totalLiters = 0;
    
    for (let i = 1; i < sortedStops.length; i++) {
      const kmDiff = sortedStops[i].odometer_km - sortedStops[i - 1].odometer_km;
      totalKm += kmDiff;
      totalLiters += sortedStops[i].liters;
    }
    
    if (totalLiters === 0) return null;
    
    return {
      kmPerLiter: totalKm / totalLiters,
      litersPerHundredKm: (totalLiters / totalKm) * 100,
      totalKm,
      totalLiters,
      avgCostPerKm: fuelStops.reduce((sum, s) => sum + s.total_cost, 0) / totalKm
    };
  } catch (error) {
    console.error('Error calculating fuel efficiency:', error);
    return null;
  }
};
